import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {
  createStageItemTarget,
  createStageTarget
} from './stage-target.js';

export const STAGE_TARGET_ENTITY_TYPE='stage_target';
export const STAGE_ITEM_TARGET_ENTITY_TYPE='stage_item_target';
export const EDITABLE_TARGET_SESSION_STATUSES=Object.freeze(['draft','planned','preparation','ready']);
const EDITABLE=new Set(EDITABLE_TARGET_SESSION_STATUSES);

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

function assertEditable(status){
  const value=String(status||'').trim().toLowerCase();
  if(!EDITABLE.has(value))throw new Error('STAGE_TARGETS_SESSION_LOCKED');
  return value;
}

function repositoryRecord(row){
  const copy={...row};
  delete copy.key;
  delete copy.entityType;
  return copy;
}

function conflict(row){
  return row?.syncStatus===SyncStatus.CONFLICT;
}

function stageTargetId(stageId){
  return required(stageId,'stageId');
}

export function stageItemTargetId(stageId,itemId){
  return required(stageId,'stageId')+':'+required(itemId,'itemId');
}

function mergeStored(existing,domain,id,{now}){
  if(existing){
    return {
      ...repositoryRecord(existing),
      ...domain,
      id,
      userId:existing.userId
    };
  }
  return {
    ...createEntityMeta({id,now,syncStatus:SyncStatus.PENDING_CREATE}),
    ...domain,
    id
  };
}

export function createStageTargetStore({
  repository,
  queue,
  userId,
  now=()=>new Date().toISOString()
}={}){
  if(!repository?.get||!repository?.list||!repository?.put)throw new TypeError('repository with get/list/put is required');
  if(!queue?.enqueue||!queue?.markConflict)throw new TypeError('sync queue is required');
  const owner=required(userId,'userId');

  function assertOwner(row){
    if(row?.userId!==owner)throw new Error('STAGE_TARGET_OWNER_MISMATCH');
    return row;
  }

  async function getStageTarget(stageId){
    const row=await repository.get(STAGE_TARGET_ENTITY_TYPE,stageTargetId(stageId));
    if(!row||row.deletedAt)return null;
    return Object.freeze(assertOwner(row));
  }

  async function saveStageTarget(stageId,input={}, {sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const id=stageTargetId(stageId);
    const current=await repository.get(STAGE_TARGET_ENTITY_TYPE,id);
    if(current&&conflict(current))throw new Error('STAGE_TARGET_CONFLICT');
    if(current)assertOwner(current);
    const domain=createStageTarget({...input,stageId:id,userId:owner});
    const entity=mergeStored(current,domain,id,{now:now()});
    delete entity.deletedAt;
    const operation=current&&Number(current.serverVersion)>0?'update':'create';
    return queue.enqueue(STAGE_TARGET_ENTITY_TYPE,entity,operation);
  }

  async function clearStageTarget(stageId,{sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const id=stageTargetId(stageId);
    const current=await repository.get(STAGE_TARGET_ENTITY_TYPE,id);
    if(!current)return null;
    assertOwner(current);
    if(conflict(current))throw new Error('STAGE_TARGET_CONFLICT');
    return queue.enqueue(STAGE_TARGET_ENTITY_TYPE,current,'delete');
  }

  async function listItemTargets(stageId){
    const stage=required(stageId,'stageId');
    const rows=await repository.list(STAGE_ITEM_TARGET_ENTITY_TYPE);
    return Object.freeze((rows||[])
      .filter(row=>row.userId===owner&&row.stageId===stage&&!row.deletedAt)
      .sort((a,b)=>String(a.itemId).localeCompare(String(b.itemId))));
  }

  async function getItemTarget(stageId,itemId){
    const id=stageItemTargetId(stageId,itemId);
    const row=await repository.get(STAGE_ITEM_TARGET_ENTITY_TYPE,id);
    if(!row||row.deletedAt)return null;
    return Object.freeze(assertOwner(row));
  }

  async function saveItemTarget(stageId,itemId,input={}, {sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const stage=required(stageId,'stageId');
    const item=required(itemId,'itemId');
    const id=stageItemTargetId(stage,item);
    const current=await repository.get(STAGE_ITEM_TARGET_ENTITY_TYPE,id);
    if(current&&conflict(current))throw new Error('STAGE_ITEM_TARGET_CONFLICT');
    if(current)assertOwner(current);
    const domain=createStageItemTarget({...input,stageId:stage,itemId:item,userId:owner});
    const entity=mergeStored(current,domain,id,{now:now()});
    delete entity.deletedAt;
    const operation=current&&Number(current.serverVersion)>0?'update':'create';
    return queue.enqueue(STAGE_ITEM_TARGET_ENTITY_TYPE,entity,operation);
  }

  async function clearItemTarget(stageId,itemId,{sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const id=stageItemTargetId(stageId,itemId);
    const current=await repository.get(STAGE_ITEM_TARGET_ENTITY_TYPE,id);
    if(!current)return null;
    assertOwner(current);
    if(conflict(current))throw new Error('STAGE_ITEM_TARGET_CONFLICT');
    return queue.enqueue(STAGE_ITEM_TARGET_ENTITY_TYPE,current,'delete');
  }

  async function markStageConflict(stageId,details){
    const current=await repository.get(STAGE_TARGET_ENTITY_TYPE,stageTargetId(stageId));
    if(!current)throw new Error('STAGE_TARGET_NOT_FOUND');
    assertOwner(current);
    return queue.markConflict(STAGE_TARGET_ENTITY_TYPE,current,details);
  }

  async function markItemConflict(stageId,itemId,details){
    const id=stageItemTargetId(stageId,itemId);
    const current=await repository.get(STAGE_ITEM_TARGET_ENTITY_TYPE,id);
    if(!current)throw new Error('STAGE_ITEM_TARGET_NOT_FOUND');
    assertOwner(current);
    return queue.markConflict(STAGE_ITEM_TARGET_ENTITY_TYPE,current,details);
  }

  return Object.freeze({
    getStageTarget,
    saveStageTarget,
    clearStageTarget,
    listItemTargets,
    getItemTarget,
    saveItemTarget,
    clearItemTarget,
    markStageConflict,
    markItemConflict
  });
}
